import React, { useEffect, useMemo, useState } from 'react';
import { FiUsers } from 'react-icons/fi';
import { adminAPI, materialsAPI } from '../../../services/api';
import StudentGroupPicker from '../../../components/StudentGroupPicker';
import CreateLessonPageBlocks from '../CreateLesson/CreateLessonPageBlocks';

const CreateLessonMaterialPage = () => {
    const [loadingStudents, setLoadingStudents] = useState(false);
    const [students, setStudents] = useState([]);

    // Назначение материала
    const [visibility, setVisibility] = useState('all'); // all | students
    const [selectedStudentIds, setSelectedStudentIds] = useState([]);
    const [selectedGroupIds, setSelectedGroupIds] = useState([]);

    useEffect(() => {
        const loadStudents = async () => {
            try {
                setLoadingStudents(true);
                const res = await adminAPI.getStudents();
                setStudents(res.data.students || []);
            } catch (e) {
                console.error('Ошибка загрузки учеников:', e);
                alert('Не удалось загрузить учеников');
            } finally {
                setLoadingStudents(false);
            }
        };

        loadStudents();
    }, []);

    const toggleStudent = (studentId) => {
        setSelectedStudentIds(prev => {
            const idNum = typeof studentId === 'number' ? studentId : parseInt(studentId, 10);
            const exists = prev.includes(idNum);
            if (exists) return prev.filter(id => id !== idNum);
            return [...prev, idNum];
        });
    };

    const targetUI = useMemo(() => {
        return (
            <div style={{ borderTop: '1px solid #eee', paddingTop: '20px' }}>
                <h3 style={{ marginBottom: '15px' }}>Кому доступен материал</h3>

                <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', marginBottom: '12px' }}>
                    <button
                        type="button"
                        onClick={() => setVisibility('all')}
                        style={{
                            padding: '10px 14px',
                            borderRadius: '8px',
                            border: visibility === 'all' ? '2px solid #52c41a' : '2px solid #e8e8e8',
                            background: visibility === 'all' ? '#f6ffed' : 'white',
                            cursor: 'pointer',
                            fontWeight: 600,
                            color: visibility === 'all' ? '#52c41a' : '#333'
                        }}
                    >
                        Всем
                    </button>

                    <button
                        type="button"
                        onClick={() => setVisibility('students')}
                        style={{
                            padding: '10px 14px',
                            borderRadius: '8px',
                            border: visibility === 'students' ? '2px solid #4a90e2' : '2px solid #e8e8e8',
                            background: visibility === 'students' ? '#f0f7ff' : 'white',
                            cursor: 'pointer',
                            fontWeight: 600,
                            color: visibility === 'students' ? '#4a90e2' : '#333'
                        }}
                    >
                        {students.length > 0 ? 'Выбранным' : 'Выбранным'}
                    </button>
                </div>

                {visibility === 'students' && (
                    <div>
                        <StudentGroupPicker
                            selectedGroupIds={selectedGroupIds}
                            onChange={setSelectedGroupIds}
                        />

                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '10px' }}>
                            <FiUsers color="#4a90e2" />
                            <div style={{ fontSize: '14px', color: '#555' }}>
                                Или отметьте учеников по отдельности:
                            </div>
                        </div>

                        {loadingStudents ? (
                            <div style={{ fontSize: '13px', color: '#999' }}>Загрузка учеников...</div>
                        ) : (
                            <div style={{ maxHeight: '220px', overflow: 'auto', paddingRight: '8px' }}>
                                {students.map((s) => {
                                    const id = s.id;
                                    const fullName = [s.first_name, s.last_name].filter(Boolean).join(' ') || s.username;
                                    const checked = selectedStudentIds.includes(id);
                                    return (
                                        <label
                                            key={id}
                                            style={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: '10px',
                                                padding: '8px 10px',
                                                borderRadius: '8px',
                                                marginBottom: '6px',
                                                background: checked ? '#e3f2fd' : '#fafafa',
                                                border: checked ? '1px solid #4a90e2' : '1px solid #eee',
                                                cursor: 'pointer'
                                            }}
                                        >
                                            <input
                                                type="checkbox"
                                                checked={checked}
                                                onChange={() => toggleStudent(id)}
                                            />
                                            <div style={{ fontWeight: 600, fontSize: '13px' }}>{fullName}</div>
                                        </label>
                                    );
                                })}
                            </div>
                        )}
                    </div>
                )}
            </div>
        );
    }, [visibility, students, selectedStudentIds, selectedGroupIds, loadingStudents]);

    return (
        <div>
            <CreateLessonPageBlocks
                pageTitleCreate="Создать урок-материал"
                pageSubtitle="Урок будет доступен студентам по выбранному назначению"
                cancelNavigateTo="/admin/materials"
                afterSaveNavigateTo="/admin/materials"
                renderExtraParams={targetUI}
                onAfterLessonCreated={async ({ lessonId, lessonTitle, lessonDescription }) => {
                    if (visibility === 'students' && selectedStudentIds.length === 0 && selectedGroupIds.length === 0) {
                        throw new Error('Выберите хотя бы одного ученика или группу');
                    }

                    const formData = new FormData();
                    formData.append('title', lessonTitle || '');
                    formData.append('description', lessonDescription || '');
                    formData.append('type', 'lesson');
                    formData.append('lesson_id', lessonId);
                    formData.append('visibility', visibility);
                    if (visibility === 'students') {
                        formData.append('studentIds', JSON.stringify(selectedStudentIds));
                        formData.append('groupIds', JSON.stringify(selectedGroupIds));
                    }

                    await materialsAPI.createMaterial(formData);
                }}
                successCreateMessage="Урок-материал успешно создан"
                successEditMessage="Урок-материал обновлен"
            />
        </div>
    );
};

export default CreateLessonMaterialPage;

